import React, { useEffect, useRef, useState } from 'react';
import { animate, motion, useInView, Variants } from 'framer-motion';
import { C, EASE, FONT_DISPLAY, signed } from './theme';

/* ─── Icons (24px grid, stroke) ───────────────────────────────── */
const ICONS = {
  spark: 'M12 3l1.9 5.6L19.5 10l-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.4z',
  grid: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z',
  layers: 'M12 3 2 8l10 5 10-5-10-5zM2 13l10 5 10-5M2 17.5l10 5 10-5',
  book: 'M4 5.5C4 4.7 4.7 4 5.5 4H11v16H5.5c-.8 0-1.5-.7-1.5-1.5zM20 5.5c0-.8-.7-1.5-1.5-1.5H13v16h5.5c.8 0 1.5-.7 1.5-1.5z',
  users: 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21c0-3.9 3.1-7 7-7s7 3.1 7 7M16 3.3a4 4 0 0 1 0 7.4M18 14.4c2.4.9 4 3.2 4 6.6',
  teacher: 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21c0-4.4 3.6-8 8-8s8 3.6 8 8',
  sheet: 'M6 3h9l4 4v14H6zM15 3v4h4M9 12h7M9 16h5',
  list: 'M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01',
  up: 'M12 19V5M5.5 11.5 12 5l6.5 6.5',
  down: 'M12 5v14M5.5 12.5 12 19l6.5-6.5',
  right: 'M5 12h14M13 6l6 6-6 6',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  x: 'M6 6l12 12M18 6 6 18',
  alert: 'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
  refresh: 'M21 12a9 9 0 0 1-15.3 6.4L3 16M3 12a9 9 0 0 1 15.3-6.4L21 8M21 3v5h-5M3 21v-5h5',
  logout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  trophy: 'M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0zM17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3',
  rocket: 'M5 15c-1.5 1.3-2 5-2 5s3.7-.5 5-2c.7-.8.7-2 0-2.8a2 2 0 0 0-3-.2zM12 15l-3-3a22 22 0 0 1 2-4A12.9 12.9 0 0 1 22 2c0 2.7-.8 7.5-6 11a22.4 22.4 0 0 1-4 2zM9 12H4s.6-3 2-4c1.6-1 5 0 5 0M12 15v5s3-.6 4-2c1-1.6 0-5 0-5',
  target: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12zM12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  pulse: 'M3 12h4l3-8 4 16 3-8h4',
  clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 7v5l3 2',
  cap: 'M2 9l10-5 10 5-10 5zM6 11v4c0 1.6 2.7 3 6 3s6-1.4 6-3v-4M22 9v6',
  flame: 'M12 22c4 0 7-2.7 7-7 0-3-2-5.5-3.5-7 0 2-1 3.5-2.5 3.5C13 8 12 5 9 2c0 4-4 6.5-4 13 0 4.3 3 7 7 7z',
};
export type IconName = keyof typeof ICONS;

export const Icon: React.FC<{ name: IconName; size?: number; color?: string; stroke?: number; style?: React.CSSProperties }> = ({
  name, size = 18, color = 'currentColor', stroke = 1.9, style,
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, ...style }} aria-hidden>
    <path d={ICONS[name]} />
  </svg>
);

/** iOS-style rounded icon tile. */
export const Tile: React.FC<{ icon: IconName; bg: string; fg: string; size?: number }> = ({ icon, bg, fg, size = 36 }) => (
  <span style={{ width: size, height: size, borderRadius: size * 0.3, background: bg, display: 'inline-grid', placeItems: 'center', flexShrink: 0 }}>
    <Icon name={icon} color={fg} size={Math.round(size * 0.52)} stroke={2} />
  </span>
);

/* ─── Motion ──────────────────────────────────────────────────── */
export const pageV: Variants = { hidden: {}, show: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } } };
export const itemV: Variants = {
  hidden: { opacity: 0, y: 26, scale: 0.985, filter: 'blur(8px)' },
  show: { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)', transition: { duration: 0.7, ease: EASE } },
};

/** Glass card that floats in as it scrolls into view. */
export const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties; dark?: boolean; className?: string; id?: string }> = ({
  children, style, dark, className, id,
}) => (
  <motion.section
    id={id}
    variants={itemV}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.15 }}
    className={`sx-card${dark ? ' sx-card--dark' : ''}${className ? ` ${className}` : ''}`}
    style={style}
  >
    {children}
  </motion.section>
);

export const CardHead: React.FC<{ title: string; cap?: React.ReactNode; right?: React.ReactNode; dark?: boolean; icon?: React.ReactNode }> = ({ title, cap, right, dark, icon }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', minWidth: 0 }}>
      {icon}
      <div style={{ minWidth: 0 }}>
        <h3 style={{ margin: 0, fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 18, letterSpacing: '-0.02em', color: dark ? '#fff' : C.ink }}>{title}</h3>
        {cap && <p style={{ margin: '3px 0 0', fontSize: 13, lineHeight: 1.45, color: dark ? C.nightMu : C.mu }}>{cap}</p>}
      </div>
    </div>
    {right && <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>{right}</div>}
  </div>
);

/** Number that counts up the first time it scrolls into view. */
export const CountUp: React.FC<{ to: number; decimals?: number; prefix?: string; suffix?: string; duration?: number; delay?: number; signed?: boolean }> = ({
  to, decimals = 0, prefix = '', suffix = '', duration = 1.3, delay = 0, signed: showSign = false,
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const ctrl = animate(0, to, { duration, delay, ease: EASE, onUpdate: setV });
    return () => ctrl.stop();
  }, [inView, to, duration, delay]);
  const abs = Math.abs(v).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  const sign = showSign ? (to > 0 ? '+' : to < 0 ? '−' : '±') : to < 0 ? '−' : '';
  return <span ref={ref} style={{ fontVariantNumeric: 'tabular-nums' }}>{prefix}{sign}{abs}{suffix}</span>;
};

/** Change pill: green when the change is good, red when bad. */
export const Delta: React.FC<{ v: number | null | undefined; unit?: string; decimals?: number; higherIsBetter?: boolean; solid?: boolean }> = ({
  v, unit = ' pts', decimals = 1, higherIsBetter = true, solid,
}) => {
  if (v == null || !Number.isFinite(v)) return <span className="sx-pill" style={{ background: 'rgba(15,20,40,0.05)', color: C.mu }}>New</span>;
  const flat = Math.abs(v) < 0.05;
  const good = higherIsBetter ? v > 0 : v < 0;
  const fg = flat ? C.mu : good ? C.greenInk : C.redInk;
  const bg = flat ? 'rgba(15,20,40,0.05)' : good ? C.greenL : C.redL;
  return (
    <span className="sx-pill" style={solid ? { background: flat ? C.mu : good ? C.green : C.red, color: '#fff' } : { background: bg, color: fg }}>
      {!flat && <Icon name={v > 0 ? 'up' : 'down'} size={11} stroke={2.6} />}
      {signed(v, decimals)}{unit}
    </span>
  );
};

export const Pill: React.FC<{ bg: string; fg: string; children: React.ReactNode; style?: React.CSSProperties }> = ({ bg, fg, children, style }) => (
  <span className="sx-pill" style={{ background: bg, color: fg, ...style }}>{children}</span>
);

export const LegendDot: React.FC<{ color: string; label: string; shape?: 'dot' | 'line' | 'ring' }> = ({ color, label, shape = 'dot' }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.mu }}>
    <span style={{
      width: shape === 'line' ? 16 : 9, height: shape === 'line' ? 3 : 9, borderRadius: 99, boxSizing: 'border-box',
      background: shape === 'ring' ? 'transparent' : color, border: shape === 'ring' ? `2px solid ${color}` : undefined,
    }} />
    {label}
  </span>
);

export const Skel: React.FC<{ h: number; w?: number | string; r?: number; style?: React.CSSProperties }> = ({ h, w = '100%', r = 18, style }) => (
  <div className="sx-skel" style={{ height: h, width: w, borderRadius: r, ...style }} />
);

/** Placeholder card for a section whose data is still streaming in. */
export const LoadingCard: React.FC<{ h?: number; label?: string }> = ({ h = 280, label }) => (
  <div className="sx-card" style={{ display: 'flex', flexDirection: 'column', gap: 14, minHeight: h }}>
    <Skel h={18} w="40%" r={8} />
    <Skel h={12} w="60%" r={6} />
    <Skel h={h - 110} />
    {label && <span style={{ fontSize: 12, color: C.mu }}>{label}</span>}
  </div>
);

export const Empty: React.FC<{ icon?: IconName; title: string; body?: React.ReactNode; dark?: boolean }> = ({ icon = 'spark', title, body, dark }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 8, padding: '36px 18px' }}>
    <Tile icon={icon} bg={dark ? 'rgba(255,255,255,0.08)' : 'rgba(15,20,40,0.05)'} fg={dark ? C.nightMu : C.mu} size={44} />
    <div style={{ fontSize: 15, fontWeight: 650, color: dark ? '#fff' : C.ink }}>{title}</div>
    {body && <div style={{ fontSize: 13, lineHeight: 1.5, maxWidth: 420, color: dark ? C.nightMu : C.mu }}>{body}</div>}
  </div>
);

/** Thin horizontal meter. */
export const Meter: React.FC<{ v: number; color: string; track?: string; h?: number; delay?: number }> = ({ v, color, track = 'rgba(15,20,40,0.07)', h = 8, delay = 0 }) => (
  <div style={{ height: h, borderRadius: 99, background: track, overflow: 'hidden' }}>
    <motion.div initial={{ width: 0 }} whileInView={{ width: `${Math.max(0, Math.min(100, v))}%` }} viewport={{ once: true }} transition={{ delay, duration: 1, ease: EASE }}
      style={{ height: '100%', borderRadius: 99, background: color }} />
  </div>
);

export const initials = (name: string) => name.split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

export const SectionTitle: React.FC<{ eyebrow?: string; title: string; sub?: string; right?: React.ReactNode }> = ({ eyebrow, title, sub, right }) => (
  <motion.div variants={itemV} initial="hidden" whileInView="show" viewport={{ once: true }}
    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginTop: 14 }}>
    <div>
      {eyebrow && <div style={{ fontSize: 12, fontWeight: 650, letterSpacing: '0.04em', textTransform: 'uppercase', color: C.blue }}>{eyebrow}</div>}
      <h2 style={{ margin: '2px 0 0', fontFamily: FONT_DISPLAY, fontSize: 24, fontWeight: 750, letterSpacing: '-0.03em' }}>{title}</h2>
      {sub && <p style={{ margin: '4px 0 0', fontSize: 14, color: C.mu }}>{sub}</p>}
    </div>
    {right}
  </motion.div>
);
